import { useCallback, useEffect, useRef, useState } from "react";
import {
  isCampaignRecipientPageSize,
  type CampaignAudiencePreviewResponse,
  type CampaignAudienceUploadPreviewResponse,
  type CampaignAudienceUploadResponse,
  type CampaignDto,
  type CampaignRecipientListItem,
  type CampaignRecipientPageSize,
  type ContactGroupSummary,
  type ConfigureCampaignAudienceUploadRequest,
} from "@multi-chat/shared";
import {
  commitCampaignAudience,
  configureCampaignAudienceUpload,
  getCampaign,
  getCampaignAudienceUpload,
  getCampaignAudienceUploadPreview,
  listCampaignRecipients,
  previewCampaignAudience,
  removeCampaignAudienceUpload,
  updateCampaignAudience,
  uploadCampaignAudienceCsv,
} from "../../api/campaigns-api.js";
import { listContactGroups } from "../../api/contacts-api.js";
import {
  campaignAudienceWorksheetNames,
  isCampaignSnapshotStaleError,
  isCampaignUploadMissingError,
} from "./campaigns-errors.js";
import {
  CAMPAIGN_IMPORT_POLL_MS,
  CAMPAIGN_RECIPIENT_PAGE_SIZE,
  shouldPollCampaignImport,
} from "./campaigns-presentation.js";

async function readUpload(
  organizationId: number,
  campaignId: number,
): Promise<{
  upload: CampaignAudienceUploadResponse["upload"];
  preview: CampaignAudienceUploadPreviewResponse | null;
}> {
  try {
    const response = await getCampaignAudienceUpload(organizationId, campaignId);
    if (!response.upload) {
      return { upload: null, preview: null };
    }
    if (response.upload.status === "READY") {
      const preview = await getCampaignAudienceUploadPreview(organizationId, campaignId);
      return { upload: response.upload, preview };
    }
    return { upload: response.upload, preview: null };
  } catch (caught) {
    if (isCampaignUploadMissingError(caught)) {
      return { upload: null, preview: null };
    }
    throw caught;
  }
}

export async function runCampaignUploadMutation<TPrepared = undefined>(input: {
  mutate: () => Promise<CampaignAudienceUploadResponse>;
  loadPreview: () => Promise<CampaignAudienceUploadPreviewResponse>;
  prepare?: (response: CampaignAudienceUploadResponse) => Promise<TPrepared>;
  commit: (settled: {
    upload: NonNullable<CampaignAudienceUploadResponse["upload"]>;
    preview: CampaignAudienceUploadPreviewResponse | null;
    prepared: TPrepared | undefined;
  }) => void;
}): Promise<void> {
  const response = await input.mutate();
  if (!response.upload) {
    throw new Error("Campaign audience upload is missing");
  }
  const upload = response.upload;
  const [preview, prepared] = await Promise.all([
    upload.status === "READY" ? input.loadPreview() : Promise.resolve(null),
    input.prepare ? input.prepare(response) : Promise.resolve(undefined),
  ]);
  input.commit({
    upload: preview?.upload ?? upload,
    preview,
    prepared,
  });
}

export function useCampaignEditor(
  organizationId: number,
  campaignId: number,
): {
  campaign: CampaignDto | null;
  groups: ContactGroupSummary[];
  upload: CampaignAudienceUploadResponse["upload"] | null;
  uploadPreview: CampaignAudienceUploadPreviewResponse | null;
  preview: CampaignAudiencePreviewResponse | null;
  recipients: CampaignRecipientListItem[];
  recipientsNextCursor: string | null;
  recipientsHasPrevious: boolean;
  loading: boolean;
  saving: boolean;
  uploading: boolean;
  uploadProgress: number | null;
  polling: boolean;
  recipientsLoading: boolean;
  recipientsStale: boolean;
  error: unknown;
  reload: () => Promise<void>;
  applyCampaignUpdate: (next: CampaignDto) => void;
  setContactIds: (contactIds: number[]) => Promise<void>;
  setGroupIds: (contactGroupIds: number[]) => Promise<void>;
  setCsvUpload: (uploadId: string) => Promise<void>;
  uploadCsv: (file: File, worksheet?: string) => Promise<void>;
  configureUpload: (body: ConfigureCampaignAudienceUploadRequest) => Promise<void>;
  removeUpload: () => Promise<void>;
  previewAudience: () => Promise<void>;
  confirmAudience: () => Promise<void>;
  recipientPageSize: CampaignRecipientPageSize;
  recipientRangeStart: number;
  recipientRangeEnd: number;
  setRecipientPageSize: (size: CampaignRecipientPageSize) => void;
  loadNextRecipients: () => Promise<void>;
  loadPreviousRecipients: () => Promise<void>;
} {
  const [campaign, setCampaign] = useState<CampaignDto | null>(null);
  const [groups, setGroups] = useState<ContactGroupSummary[]>([]);
  const [upload, setUpload] = useState<CampaignAudienceUploadResponse["upload"] | null>(null);
  const [uploadPreview, setUploadPreview] =
    useState<CampaignAudienceUploadPreviewResponse | null>(null);
  const [preview, setPreview] = useState<CampaignAudiencePreviewResponse | null>(null);
  const [recipients, setRecipients] = useState<CampaignRecipientListItem[]>([]);
  const [recipientsNextCursor, setRecipientsNextCursor] = useState<string | null>(null);
  const [recipientCursor, setRecipientCursor] = useState<string | undefined>(undefined);
  const [recipientHistory, setRecipientHistory] = useState<Array<string | undefined>>([]);
  const [recipientPageSize, setRecipientPageSizeState] =
    useState<CampaignRecipientPageSize>(CAMPAIGN_RECIPIENT_PAGE_SIZE);
  const [recipientOffset, setRecipientOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [polling, setPolling] = useState(false);
  const [recipientsLoading, setRecipientsLoading] = useState(false);
  const [recipientsStale, setRecipientsStale] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const previewRevisionRef = useRef<number | null>(null);
  const snapshotStale = campaign?.snapshotStale ?? true;

  const applyCampaign = useCallback((next: CampaignDto) => {
    setCampaign(next);
    if (
      previewRevisionRef.current != null &&
      previewRevisionRef.current !== next.audienceDefinitionRevision
    ) {
      setPreview(null);
      previewRevisionRef.current = null;
    }
  }, []);

  const refreshUpload = useCallback(async () => {
    const result = await readUpload(organizationId, campaignId);
    setUpload(result.upload);
    setUploadPreview(result.preview);
  }, [campaignId, organizationId]);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [campaignResponse, groupRows] = await Promise.all([
        getCampaign(organizationId, campaignId),
        listContactGroups(organizationId),
      ]);
      applyCampaign(campaignResponse.campaign);
      setGroups(groupRows);
      if (campaignResponse.campaign.audienceDefinition.upload?.uploadId) {
        const uploadResult = await readUpload(organizationId, campaignId);
        setUpload(uploadResult.upload);
        setUploadPreview(uploadResult.preview);
      } else {
        setUpload(null);
        setUploadPreview(null);
      }
    } catch (caught) {
      setError(caught);
    } finally {
      setLoading(false);
    }
  }, [applyCampaign, campaignId, organizationId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  useEffect(() => {
    if (!shouldPollCampaignImport(upload?.status ?? null)) {
      setPolling(false);
      return;
    }
    setPolling(true);
    const timer = window.setInterval(() => {
      void refreshUpload().catch((caught) => setError(caught));
    }, CAMPAIGN_IMPORT_POLL_MS);
    return () => window.clearInterval(timer);
  }, [refreshUpload, upload?.status]);

  useEffect(() => {
    if (snapshotStale) {
      setRecipients([]);
      setRecipientsNextCursor(null);
      setRecipientOffset(0);
      setRecipientsLoading(false);
      return;
    }
    let cancelled = false;
    setRecipientsLoading(true);
    setRecipientsStale(false);
    void listCampaignRecipients(organizationId, campaignId, {
      cursor: recipientCursor,
      limit: recipientPageSize,
    })
      .then((response) => {
        if (cancelled) return;
        setRecipients(response.items);
        setRecipientsNextCursor(response.nextCursor);
      })
      .catch((caught) => {
        if (cancelled) return;
        if (isCampaignSnapshotStaleError(caught)) {
          setRecipients([]);
          setRecipientsNextCursor(null);
          setRecipientsStale(true);
        } else {
          setError(caught);
        }
      })
      .finally(() => {
        if (!cancelled) setRecipientsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, organizationId, recipientCursor, recipientPageSize, snapshotStale]);

  const resetRecipientPager = useCallback(() => {
    setRecipientCursor(undefined);
    setRecipientHistory([]);
    setRecipientOffset(0);
  }, []);

  const setRecipientPageSize = useCallback((size: CampaignRecipientPageSize) => {
    if (!isCampaignRecipientPageSize(size)) return;
    setRecipientPageSizeState(size);
    setRecipients([]);
    resetRecipientPager();
  }, [resetRecipientPager]);

  const runMutation = useCallback(
    async (work: () => Promise<CampaignDto>) => {
      setSaving(true);
      setError(null);
      try {
        const next = await work();
        applyCampaign(next);
        resetRecipientPager();
      } catch (caught) {
        setError(caught);
        throw caught;
      } finally {
        setSaving(false);
      }
    },
    [applyCampaign, resetRecipientPager],
  );

  const setContactIds = useCallback(
    async (contactIds: number[]) => {
      await runMutation(async () => {
        const response = await updateCampaignAudience(organizationId, campaignId, { contactIds });
        return response.campaign;
      });
    },
    [campaignId, organizationId, runMutation],
  );

  const setGroupIds = useCallback(
    async (contactGroupIds: number[]) => {
      await runMutation(async () => {
        const response = await updateCampaignAudience(organizationId, campaignId, {
          contactGroupIds,
        });
        return response.campaign;
      });
    },
    [campaignId, organizationId, runMutation],
  );

  const setCsvUpload = useCallback(
    async (uploadId: string) => {
      await runMutation(async () => {
        const response = await updateCampaignAudience(organizationId, campaignId, {
          upload: { uploadId },
        });
        return response.campaign;
      });
    },
    [campaignId, organizationId, runMutation],
  );

  const uploadCsv = useCallback(
    async (file: File, worksheet?: string) => {
      setUploading(true);
      setUploadProgress(0);
      setError(null);
      try {
        await runCampaignUploadMutation<CampaignDto>({
          mutate: () =>
            uploadCampaignAudienceCsv(organizationId, campaignId, file, {
              onProgress: setUploadProgress,
              idempotencyKey: crypto.randomUUID(),
              worksheet,
            }),
          loadPreview: () => getCampaignAudienceUploadPreview(organizationId, campaignId),
          prepare: async () => {
            const refreshed = await getCampaign(organizationId, campaignId);
            return refreshed.campaign;
          },
          commit: (settled) => {
            if (settled.prepared) applyCampaign(settled.prepared);
            setUpload(settled.upload);
            setUploadPreview(settled.preview);
            setPreview(null);
            previewRevisionRef.current = null;
            resetRecipientPager();
          },
        });
      } catch (caught) {
        if (campaignAudienceWorksheetNames(caught) == null) setError(caught);
        throw caught;
      } finally {
        setUploading(false);
        setUploadProgress(null);
      }
    },
    [applyCampaign, campaignId, organizationId, resetRecipientPager],
  );

  const configureUpload = useCallback(
    async (body: ConfigureCampaignAudienceUploadRequest) => {
      setSaving(true);
      setError(null);
      try {
        await runCampaignUploadMutation<CampaignDto>({
          mutate: () => configureCampaignAudienceUpload(organizationId, campaignId, body),
          loadPreview: () => getCampaignAudienceUploadPreview(organizationId, campaignId),
          prepare: async () => {
            const refreshed = await getCampaign(organizationId, campaignId);
            return refreshed.campaign;
          },
          commit: (settled) => {
            if (settled.prepared) applyCampaign(settled.prepared);
            setUpload(settled.upload);
            setUploadPreview(settled.preview);
            setPreview(null);
            previewRevisionRef.current = null;
          },
        });
      } catch (caught) {
        setError(caught);
        throw caught;
      } finally {
        setSaving(false);
      }
    },
    [applyCampaign, campaignId, organizationId],
  );

  const removeUpload = useCallback(async () => {
    await runMutation(async () => {
      await removeCampaignAudienceUpload(organizationId, campaignId);
      setUpload(null);
      setUploadPreview(null);
      const refreshed = await getCampaign(organizationId, campaignId);
      return refreshed.campaign;
    });
  }, [campaignId, organizationId, runMutation]);

  const previewAudience = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await previewCampaignAudience(organizationId, campaignId);
      setPreview(response);
      previewRevisionRef.current = response.definitionRevision;
    } catch (caught) {
      setError(caught);
      throw caught;
    } finally {
      setSaving(false);
    }
  }, [campaignId, organizationId]);

  const confirmAudience = useCallback(async () => {
    setSaving(true);
    setError(null);
    try {
      const response = await commitCampaignAudience(organizationId, campaignId);
      applyCampaign(response.campaign);
      setPreview({
        definitionRevision: response.campaign.audienceDefinitionRevision,
        snapshotStale: false,
        summary: response.summary,
        recipientSamples: [],
        issueSamples: [],
      });
      previewRevisionRef.current = response.campaign.audienceDefinitionRevision;
      resetRecipientPager();
      await refreshUpload().catch(() => undefined);
    } catch (caught) {
      setError(caught);
      throw caught;
    } finally {
      setSaving(false);
    }
  }, [applyCampaign, campaignId, organizationId, refreshUpload, resetRecipientPager]);

  const loadNextRecipients = useCallback(async () => {
    if (!recipientsNextCursor) return;
    setRecipientHistory((history) => [...history, recipientCursor]);
    setRecipientOffset((offset) => offset + recipients.length);
    setRecipientCursor(recipientsNextCursor);
  }, [recipientCursor, recipients.length, recipientsNextCursor]);

  const loadPreviousRecipients = useCallback(async () => {
    setRecipientHistory((history) => {
      if (history.length === 0) return history;
      const previous = history[history.length - 1];
      setRecipientCursor(previous);
      return history.slice(0, -1);
    });
    setRecipientOffset((offset) => Math.max(0, offset - recipientPageSize));
  }, [recipientPageSize]);

  return {
    campaign,
    groups,
    upload,
    uploadPreview,
    preview,
    recipients,
    recipientsNextCursor,
    recipientsHasPrevious: recipientHistory.length > 0,
    recipientPageSize,
    recipientRangeStart: recipients.length === 0 ? 0 : recipientOffset + 1,
    recipientRangeEnd: recipientOffset + recipients.length,
    loading,
    saving,
    uploading,
    uploadProgress,
    polling,
    recipientsLoading,
    recipientsStale,
    error,
    reload,
    applyCampaignUpdate: applyCampaign,
    setContactIds,
    setGroupIds,
    setCsvUpload,
    uploadCsv,
    configureUpload,
    removeUpload,
    previewAudience,
    confirmAudience,
    setRecipientPageSize,
    loadNextRecipients,
    loadPreviousRecipients,
  };
}
